'use client'

import { AnimatePresence, motion } from 'motion/react'
import { type ReactNode, useRef, useState } from 'react'
import {
  FOOTER_LLM_CONVERSATIONS,
  FOOTER_LLM_KEYS,
  FOOTER_LLM_LABELS,
} from '@/modules/shared/lib/constants'

type FooterLlmKey = keyof typeof FOOTER_LLM_CONVERSATIONS

const FOOTER_LLM_ICONS: Record<FooterLlmKey, ReactNode> = {
  chatgpt: (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
    </svg>
  ),
  claude: (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="m4.7144 15.9555 4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 1.9064 1.4754 2.4893 1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 1.0686 2.0035 1.8092 2.5075 2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 17.959l-1.1414-1.9428-.1397.079-.674 7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z" />
    </svg>
  ),
  grok: (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" fillRule="evenodd">
      <path d="M9.27 15.29l7.978-5.897c.391-.29.95-.177 1.137.272.98 2.369.542 5.215-1.41 7.169-1.951 1.954-4.667 2.382-7.149 1.406l-2.711 1.257c3.889 2.661 8.611 2.003 11.562-.953 2.341-2.344 3.066-5.539 2.388-8.42l.006.007c-.983-4.232.242-5.924 2.75-9.383.06-.082.12-.164.179-.248l-3.301 3.305v-.01L9.267 15.292M7.623 16.723c-2.792-2.67-2.31-6.801.071-9.184 1.761-1.763 4.647-2.483 7.166-1.425l2.705-1.25a7.808 7.808 0 00-1.829-1A8.975 8.975 0 005.984 5.83c-2.533 2.536-3.33 6.436-1.962 9.764 1.022 2.487-.653 4.246-2.34 6.022-.599.63-1.199 1.259-1.682 1.925l7.62-6.815" />
    </svg>
  ),
}

function FooterLlmSection() {
  const [activeLlm, setActiveLlm] = useState<FooterLlmKey | null>(null)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null)

  const handleLlmHover = (key: FooterLlmKey) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
    setActiveLlm(key)
  }

  const handleCardMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setActiveLlm(null)
    }, 100)
  }

  return (
    <div className="relative" onMouseLeave={handleCardMouseLeave}>
      <AnimatePresence>
        {activeLlm && (
          <div className="absolute -bottom-8 -left-9 w-[342px] overflow-hidden px-4 pb-4">
            <motion.div
              initial={{ height: 0, scale: 0.85, opacity: 0 }}
              animate={{ height: 'auto', scale: 1, opacity: 1 }}
              exit={{ height: 0, scale: 0.85, opacity: 0 }}
              transition={{
                height: { duration: 0.25, ease: [0.4, 0, 0.2, 1] },
                scale: { duration: 0.25, ease: 'easeOut' },
                opacity: { duration: 0.2, delay: 0.1, ease: 'easeOut' },
              }}
              className="border-border bg-card overflow-hidden rounded-xl border shadow-lg"
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: {
                    duration: 0.2,
                    delay: 0.1,
                    ease: 'easeOut',
                  },
                }}
                className="px-5 pt-1 pb-16"
              >
                <AnimatePresence mode="wait">
                  <motion.ul
                    key={activeLlm}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                  >
                    {FOOTER_LLM_CONVERSATIONS[activeLlm].map((conv) => (
                      <li key={conv.href}>
                        <a
                          href={conv.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-foreground hover:text-primary group/link flex items-center justify-between gap-4 py-2.5 transition-colors"
                        >
                          <span className="flex items-center gap-2 text-sm font-medium">
                            {conv.label}
                            <svg
                              className="h-3.5 w-3.5 opacity-60"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden
                            >
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                              <polyline points="15 3 21 3 21 9" />
                              <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                          </span>
                          <span className="border-border text-foreground/70 rounded-md border px-3 py-1 text-xs font-medium">
                            {FOOTER_LLM_LABELS[activeLlm]}
                          </span>
                        </a>
                      </li>
                    ))}
                  </motion.ul>
                </AnimatePresence>
              </motion.div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div
        className={`relative z-10 flex items-center gap-6 border-t pt-3 ${activeLlm ? 'border-border' : 'border-transparent'}`}
      >
        {FOOTER_LLM_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            onMouseEnter={() => {
              handleLlmHover(key)
            }}
            className={`flex cursor-pointer items-center gap-2 text-sm transition-colors ${
              activeLlm === key
                ? 'text-foreground'
                : activeLlm
                  ? 'text-muted-foreground/50 hover:text-muted-foreground'
                  : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {FOOTER_LLM_ICONS[key]}
            {FOOTER_LLM_LABELS[key]}
          </button>
        ))}
      </div>
    </div>
  )
}

export { FooterLlmSection }
